import { useRef } from "react";
import { startOfDay, isBefore, isAfter, endOfDay, addWeeks } from "date-fns";

type CoverageType = {
    isFullyCovered: (start: Date, end: Date) => boolean;
    getUnfetchedRange: (start: Date, end: Date) => { start: Date; end: Date }[];
    updateCoverageRange: (start: Date, end: Date) => void;
}

export const useCoverage = (): CoverageType => {

    // Initialize with null - nothing is covered initially
    const cachedRangeRef = useRef<{ start: Date | null; end: Date | null }>({
        start: null,
        end: null
    });

    const isFullyCovered = (start: Date, end: Date) => {
        // If we haven't cached anything yet, nothing is covered
        if (!cachedRangeRef.current.start || !cachedRangeRef.current.end) {
            return false;
        }

        const requestedStart = startOfDay(start);
        const requestedEnd = endOfDay(end);

        // Check if the requested range is already fully covered by our cached range
        const isFullyCovered =
            !isBefore(requestedStart, cachedRangeRef.current.start) &&
            !isAfter(requestedEnd, cachedRangeRef.current.end);

        return isFullyCovered;
    }

    const updateCoverageRange = (start: Date, end: Date) => {
        const requestedStart = startOfDay(start);
        const requestedEnd = endOfDay(end);

        if (!cachedRangeRef.current.start || !cachedRangeRef.current.end) {
            cachedRangeRef.current.start = requestedStart;
            cachedRangeRef.current.end = requestedEnd;
            return;
        }

        // Expand the cached range to include the requested range
        if (requestedStart < cachedRangeRef.current.start) {
            cachedRangeRef.current.start = requestedStart;
        }
        if (requestedEnd > cachedRangeRef.current.end) {
            cachedRangeRef.current.end = requestedEnd;
        }
    }

    const getUnfetchedRange = (start: Date, end: Date) => {

        console.log("[useCoverage] getUnfetchedRange called", start, end, cachedRangeRef.current);

        const requestedStart = startOfDay(start);
        const requestedEnd = endOfDay(end);

        let rangesToFetch: { start: Date; end: Date }[] = [];

        // If nothing is cached yet, fetch the entire requested range
        if (!cachedRangeRef.current.start || !cachedRangeRef.current.end) {
            return [{ start: requestedStart, end: requestedEnd }];
        }

        // Check if we need events before our cached range
        if (isBefore(requestedStart, cachedRangeRef.current.start)) {
            // We need to fetch backwards from the cached start
            const fetchEnd = cachedRangeRef.current.start;
            let currentEnd = fetchEnd;

            // Build chunks in reverse (going backwards in time)
            const backwardChunks: { start: Date; end: Date }[] = [];
            while (isAfter(currentEnd, requestedStart)) {
                const chunkEnd = currentEnd;
                const chunkStart = addWeeks(currentEnd, -1); // Go back one week

                backwardChunks.push({ start: chunkStart, end: chunkEnd });
                currentEnd = chunkStart;
            }

            // Add backwards chunks to the beginning (they're in reverse order)
            rangesToFetch.push(...backwardChunks.reverse());
        }

        // Check if we need events after our cached range
        if (isAfter(requestedEnd, cachedRangeRef.current.end)) {
            // Start fetching from where our cache ends
            const fetchStart = cachedRangeRef.current.end;
            let currentStart = fetchStart;

            // Fetch in full week chunks until we cover the requested end date
            // Always fetch complete weeks, even if requestedEnd is in the middle of a week
            while (isBefore(currentStart, requestedEnd)) {
                const chunkStart = currentStart;
                const chunkEnd = addWeeks(currentStart, 1); // Always fetch a full week

                rangesToFetch.push({ start: chunkStart, end: chunkEnd });
                currentStart = chunkEnd;
            }
        }

        return rangesToFetch
    }



    return {
        isFullyCovered,
        getUnfetchedRange,
        updateCoverageRange,
    }
}